import { Scrollbar } from "../scrollbar/Scrollbar";
import { PartialOptions } from "overlayscrollbars";
import Tree, { TreeNodeProps } from "rc-tree";
import { NodeDragEventParams } from "rc-tree/lib/contextTypes";
import { EventDataNode, FieldDataNode, Key } from "rc-tree/lib/interface";
import { AllowDropOptions } from "rc-tree/lib/Tree";
import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { FilePath, FilePathType, SourceFile } from "../../types";
import { isSupportedTextType } from "../../utils/MediaTypes";
import { FileUpload, Text } from "@itwin/itwinui-react";
import { rename } from "./actions/rename";
import { ContextMenu } from "./context-menu/ContextMenu";
import { ContextMenuType, getContextMenuItems } from "./context-menu/getContextMenuItems";
import { DeleteDialog, NewFileDialog, RenameDialog } from "./dialogs";
import styles from "./Explorer.module.css";
import { FileExplorerIcon } from "./FileExplorerIcon";
import { depthFirstSearch, ExplorerDataNode, getTreeNodes } from "./treeDataManipulation";
import { FileUploadPlaceholder } from "./upload-placeholder/FileUploadPlaceholder";
import "./rc-tree.scss";

export interface FileExplorerOptions {
  files: SourceFile[],
  filePathSelected?: FilePath,
  callbacks?: FileExplorerCallbacks;
  newFile?: boolean;
  newFolder?: boolean;
  upload?: boolean;
}

export interface FileExplorerCallbacks {
  onFileAdded?: (filePath: FilePath, type: FilePathType, openInEditor: boolean, content?: string) => void;
  onFileRemoved?: (filePath: FilePath[]) => void;
  onRenamed?: (filePath: FilePath[][]) => void;
  onEntryChange?: (filePath: FilePath) => void;
  onSelect?: (filePath: FilePath) => void;
  onClose?: () => void;
}

const scrollOptions: PartialOptions = {

  overflow: {
    x: "hidden",
    y: "scroll"
  },
  scrollbars: {
    visibility: "auto",
    autoHide: "leave",
    autoHideDelay: 100,
  },
};

export interface ContextMenuState {
  type: ContextMenuType;
  targetNode: FieldDataNode<ExplorerDataNode>[];
}

const getDefaultSelected = (dataNode: FieldDataNode<ExplorerDataNode>, filePathSelected?: FilePath) => {
  if (filePathSelected) {
    const node = depthFirstSearch(dataNode, filePathSelected);
    return node ? [node] : [];
  }
  return [];
};

export const Explorer = ({ files, filePathSelected, callbacks, newFile, newFolder, upload }: FileExplorerOptions) => {
  const dataNodes = useMemo(() => getTreeNodes(files), [files]);
  const entry = useMemo(() => {
    const file = files.find((item) => item.entry);
    return file?.filePathRenamed ?? file?.filePath;
  }, [files]);
  const [selected, setSelected] = useState<FieldDataNode<ExplorerDataNode>[]>(() => getDefaultSelected(dataNodes, filePathSelected));
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({ type: "default", targetNode: [dataNodes] });
  const [isDragActive, setIsDragActive] = useState<boolean>(false);

  const [newFileDialog, setNewFileDialog] = useState(false);
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [renameFileDialog, setRenameFileDialog] = useState(false);
  const [deleteFileDialog, setDeleteFileDialog] = useState(false);

  useEffect(() => {
    if (filePathSelected)
      setSelected(getDefaultSelected(dataNodes, filePathSelected));
    else
      setSelected([]);
  }, [filePathSelected]);

  const onNodeSelect = useCallback((_selectedKeys: Key[], info: { node: EventDataNode<ExplorerDataNode>; selectedNodes: ExplorerDataNode[]; nativeEvent: { ctrlKey: boolean, shiftKey: boolean }; }) => {
    if (info.selectedNodes.length) {
      if (info.nativeEvent.ctrlKey) {
        setSelected(info.selectedNodes);
      } else if (info.nativeEvent.shiftKey) {
        if (info.selectedNodes.length > 1) {
          setSelected((prev) => {
            const node = info.node.parentNode;
            if (node && node.children) {
              const children = node.children!;
              const lastSelected = prev[prev.length - 1];
              const startingPos = children.findIndex((node) => node.key === lastSelected.key);
              const endingPos = children.findIndex((node) => node.key === info.node.key);

              if (startingPos > -1 && endingPos > -1) {
                const selectedVal = [];
                for (let i = Math.min(startingPos, endingPos); i <= Math.max(startingPos, endingPos); i++) {
                  selectedVal.push(children[i]);
                }

                return selectedVal;
              }
            }
            return prev;
          });
        } else {
          setSelected(info.selectedNodes);
        }
      } else {
        setSelected([info.node])

        if (info.node.isLeaf && callbacks && callbacks.onSelect) {
          callbacks.onSelect(info.node.key as string);
        }
      }
    }
  }, [callbacks]);

  useEffect(() => {
    const onDragOverHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onDragEnterHandler = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // only set active if a file is dragged over
      if (e.dataTransfer?.items?.[0]?.kind === 'file') {
        setIsDragActive((prev) => !prev ? true : prev)
      }
    };

    const onDragLeaveHandler = (e: Event) => {
      const from = (e as any).relatedTarget || (e as any).toElement;
      if (!from || from.nodeName === "HTML") {
        setIsDragActive((prev) => prev ? false : prev);
      }
    };

    window.addEventListener('dragover', onDragOverHandler);
    window.addEventListener("dragenter", onDragEnterHandler);
    window.addEventListener("dragleave", onDragLeaveHandler);
    window.addEventListener("mouseout", onDragLeaveHandler);

    return () => {
      window.removeEventListener('dragover', onDragOverHandler);
      window.removeEventListener("dragenter", onDragEnterHandler);
      window.removeEventListener("dragleave", onDragLeaveHandler);
      window.removeEventListener("mouseout", onDragLeaveHandler);
    }
  }, []);

  useEffect(() => {
    if (newFile !== undefined)
      setNewFileDialog(newFile);
    if (newFolder !== undefined)
      setNewFolderDialog(newFolder);
    if (upload) {
      const basePath = selected.length ? selected[0].key : "/";
      uploadFiles(basePath);
    }

    if (selected.length === 1)
      setContextMenu((state) => {
        return {
          ...state,
          targetNode: selected
        }
      });
    else
      setContextMenu((state) => {
        return {
          ...state,
          targetNode: [dataNodes]
        }
      });
  }, [newFile, newFolder, upload, dataNodes]);

  const { menuItems } = useMemo(() => getContextMenuItems({
    targetNode: contextMenu.targetNode,
    type: contextMenu.type,
    onNewFile: () => setNewFileDialog(true),
    onNewFolder: () => setNewFolderDialog(true),
    onRename: () => setRenameFileDialog(true),
    onDelete: () => setDeleteFileDialog(true),
    onEntry: (filePath) => callbacks && callbacks.onEntryChange && callbacks.onEntryChange(filePath),
    onUpload: async (baseFilePath: string) => uploadFiles(baseFilePath)
  }), [contextMenu]);

  const onFileAdded = (filePath?: FilePath) => {
    if (filePath && callbacks && callbacks.onFileAdded)
      callbacks.onFileAdded(filePath, "file", true);

    callbacks && callbacks.onClose && callbacks.onClose();
    setNewFileDialog(false);
  };

  const onFolderAdded = (filePath?: FilePath) => {
    if (filePath && callbacks && callbacks.onFileAdded)
      callbacks.onFileAdded(filePath, "folder", false);

    callbacks && callbacks.onClose && callbacks.onClose();
    setNewFolderDialog(false);
  };

  const onRename = (filePath?: FilePath[][]) => {
    if (filePath && callbacks && callbacks.onRenamed)
      callbacks.onRenamed(filePath);

    setRenameFileDialog(false);
  }

  const onDelete = (filePath?: FilePath[]) => {
    if (filePath && callbacks && callbacks.onFileRemoved)
      callbacks.onFileRemoved(filePath);

    setDeleteFileDialog(false);
  }

  const onDrop = (info: NodeDragEventParams<ExplorerDataNode> & {
    dragNode: EventDataNode<ExplorerDataNode>;
    dragNodesKeys: Key[];
    dropPosition: number;
    dropToGap: boolean;
  }) => {
    const destinationNode = info.node

    if (destinationNode.readOnly)
      return;

    const destinationPath = destinationNode.isLeaf ? destinationNode.parentNode?.key ?? "/" : destinationNode.key;

    let nodes = selected;
    if (nodes.length <= 1) {
      nodes = [info.dragNode];
    }

    const dirname = (val: string) => {
      const index = val.lastIndexOf("/");
      return val.substring(0, index + 1);
    }

    for (const node of nodes) {
      const result = rename(dirname(node.key), destinationPath.endsWith("/") ? destinationPath : `${destinationPath}/`, node);
      onRename(result);
    }
  };

  const dropIndicatorRender = (props: { dropPosition: -1 | 0 | 1; }) => {
    if (props.dropPosition === 1) {
      return <div className={"drop-indicator"}></div>;
    }
  };

  const onUploadFilesChange = async (files: FileList, basePath: string) => {
    setIsDragActive(false);
    const onFileAdded = callbacks && callbacks.onFileAdded ? callbacks.onFileAdded : () => { };
    try {
      for (const file of files) {
        if (!file.size) {
          onFileAdded(file.name, "folder", false);
          continue;
        }

        const content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          if (isSupportedTextType(file.name)) {
            reader.readAsText(file);
          } else {
            reader.readAsDataURL(file);
          }
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(Error(`Error occurred while reading file: ${file.name}`));
        });
        const fileName = file.name.startsWith("/") ? file.name : `/${file.name}`;
        const filePath = basePath === "/" ? fileName : `${basePath}${fileName}`;
        onFileAdded(filePath, "file", false, content);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const uploadFiles = async (baseFilePath: string) => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("style", "display: none");
    input.setAttribute("multiple", "true");
    input.onchange = async () => {
      if (input.files) {
        await onUploadFilesChange(input.files, baseFilePath);
      }

      input.remove()
    }

    document.body.appendChild(input);
    input.click();
    callbacks && callbacks.onClose && callbacks.onClose();
  };

  const treeComponent = useMemo(() => {
    return (
      <Tree<ExplorerDataNode>
        treeData={dataNodes.children}
        selectedKeys={selected.map((val) => val.key)}
        checkedKeys={entry ? [entry] : undefined}
        onDrop={onDrop}
        allowDrop={(options: AllowDropOptions<ExplorerDataNode>) => !options.dropNode.readOnly}
        dropIndicatorRender={dropIndicatorRender}
        autoExpandParent
        checkable
        defaultExpandParent
        multiple
        draggable={(node) => node.className !== "optional-file"}  // There is no custom properties on DataNode to differentiate optional files
        switcherIcon={(options: TreeNodeProps<FieldDataNode<ExplorerDataNode>>) => <FileExplorerIcon options={options} />}
        titleRender={(node) => <Text
          isMuted={node.readOnly}
          className={[
            styles.nodeTitle,
            node.readOnly ? styles.readOnly : ""].join(" ")}
          data-test={node.type === "file" ? "atp-node-file" : node.type === "folder" ? "atp-node-folder" : ""}
          data-file-path={node.key}>
          {node.title}
        </Text>}
        onRightClick={({ event, node }) => {
          if (selected.length <= 1) {

            if (node.isLeaf)
              setContextMenu({ type: "file", targetNode: [node] });
            else
              setContextMenu({ type: "folder", targetNode: [node] });

          } else {
            setContextMenu({ type: "batch", targetNode: selected });
          }

          event.preventDefault();
          event.stopPropagation();
        }}
        onSelect={onNodeSelect}
      />

    );
  }, [dataNodes, entry, selected]);

  return (
    <div className={styles.explorerContainer} data-testid="atp-file-explorer">
      <div>
        <NewFileDialog isOpen={newFileDialog} onClose={onFileAdded} isFolder={false} targetNode={contextMenu.targetNode.length ? contextMenu.targetNode[0] : dataNodes} />
        <NewFileDialog isOpen={newFolderDialog} onClose={onFolderAdded} isFolder={true} targetNode={contextMenu.targetNode.length ? contextMenu.targetNode[0] : dataNodes} />
        <RenameDialog isOpen={renameFileDialog} onClose={onRename} targetNode={contextMenu.targetNode.length ? contextMenu.targetNode[0] : undefined} />
        <DeleteDialog isOpen={deleteFileDialog} onClose={onDelete} targetNode={contextMenu.targetNode} />
      </div>
      <Scrollbar options={scrollOptions} className="explorer-pane" onContextMenu={(event: any) => {
        setContextMenu({ type: "default", targetNode: [dataNodes] });
        event.preventDefault();
        event.stopPropagation();
      }} >
        <ContextMenu items={menuItems}>
          <FileUpload className={[styles.fileUpload, isDragActive ? "iui-drag" : ""].join(" ")} onFileDropped={(files) => onUploadFilesChange(files, "/")} dragContent={<FileUploadPlaceholder />}>
            {treeComponent}
          </FileUpload>
        </ContextMenu >
      </Scrollbar >
    </div>
  )
};

export const ExplorerMemo = memo(Explorer, (oldProps, newProps) => {
  const oldFiles = oldProps.files;
  const newFiles = newProps.files;

  if (oldFiles.length !== newFiles.length)
    return false;

  if (!Object.is(oldProps.callbacks, newProps.callbacks) ||
    oldProps.filePathSelected !== newProps.filePathSelected ||
    oldProps.newFile !== newProps.newFile ||
    oldProps.newFolder !== newProps.newFolder ||
    oldProps.upload !== newProps.upload
  )
    return false;

  for (const file of newFiles) {

    const oldFile = oldFiles.find((f) => f.filePath === file.filePath);
    if (!oldFile)
      return false;

    if (oldFile.filePathRenamed !== file.filePathRenamed ||
      oldFile.entry !== file.entry ||
      oldFile.deleted !== file.deleted ||
      oldFile.readOnly !== file.readOnly)
      return false;
  }

  return true;
});
